import { DriverClosingRecordStatus } from '../../../../generated/graphql';

export const DRIVER_CLOSING_TIMEZONE = 'Europe/Tirane';

export function toBusinessLocalInput(value: Date | null | undefined): string | undefined {
  if (!value) return undefined;
  return [
    value.getFullYear(),
    pad(value.getMonth() + 1),
    pad(value.getDate()),
  ].join('-') + `T${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

export function businessDateToPickerValue(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DRIVER_CLOSING_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(parsed);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((item) => item.type === type)?.value ?? 0);
  return new Date(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
  );
}

export function formatBusinessDateTime(value: string | Date | null | undefined): string {
  if (!value) return 'Not set';
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not set';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: DRIVER_CLOSING_TIMEZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(parsed);
}

export function formatMoney(value: string | number | null | undefined): string {
  const amount = Number(value ?? 0);
  return `ALL ${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0)}`;
}

export function normalizeMoneyInput(value: string | number | null | undefined): string {
  const normalized = String(value ?? '').trim().replace(',', '.');
  if (normalized === '') return '0';
  return /^\d+(\.\d{0,2})?$/.test(normalized) ? normalized : '0';
}

export function moneyCents(value: string | number | null | undefined): bigint {
  const normalized = normalizeMoneyInput(value);
  const [whole, decimal = ''] = normalized.split('.');
  return BigInt(whole) * 100n + BigInt((decimal + '00').slice(0, 2));
}

export function centsToMoney(value: bigint): string {
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const whole = absolute / 100n;
  const decimal = absolute % 100n;
  return `${negative ? '-' : ''}${whole}.${decimal.toString().padStart(2, '0')}`;
}

export function statusLabel(status: DriverClosingRecordStatus): string {
  return status === DriverClosingRecordStatus.Finalized ? 'Finalized' : 'Draft';
}

export function enumLabel(value: string | null | undefined): string {
  if (!value) return 'All';
  return value.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
